import styled from '@emotion/styled';
import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import React, { forwardRef, HTMLAttributes, KeyboardEventHandler, PropsWithChildren, useRef, useCallback } from 'react';
import { semantic_colors, text_styles } from '../../foundation';
import { IconDotsVertical, IconProps } from '../icons/generated';
import { useBoxTabContext } from './BoxTabs';

type BaseBoxTabProps = Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> & {
  /**
   * 탭 ID
   */
  id: string;
  /**
   * 비활성화 여부
   * @default false
   */
  disabled?: boolean;
  /**
   * 액션 아이콘 컴포넌트
   * @default IconDotsVertical
   */
  iconComponent?: React.ComponentType<IconProps>;
  /**
   * 탭 선택 이벤트
   * 클릭된 탭 ID를 반환
   */
  onTabSelect?: (id: string) => void;
};

type ActionProps =
  | {
      /**
       * 액션 버튼
       */
      action: {
        onClick?: (id: string, e: React.MouseEvent) => void;
      };
      renderAction?: never;
    }
  | {
      action?: never;
      /**
       * 커스텀 액션 렌더링 함수
       */
      renderAction: (action: {
        iconComponent: React.ComponentType<IconProps>;
        iconProps: IconProps;
      }) => React.ReactNode;
    }
  | {
      action?: never;
      renderAction?: never;
    };

export type BoxTabProps = PropsWithChildren<BaseBoxTabProps & ActionProps>;

export const BoxTab = forwardRef<HTMLDivElement, BoxTabProps>(
  (
    {
      id,
      disabled = false,
      iconComponent: ActionIcon = IconDotsVertical,
      onTabSelect,
      className,
      children,
      action,
      renderAction,
      ...props
    },
    ref,
  ) => {
    const { activeTabId, onChange } = useBoxTabContext();

    const tabRef = useRef<HTMLDivElement | null>(null);
    const mergedRef = useMergeRefs([tabRef, ref]);

    const selected = activeTabId === id;

    const showAction = typeof renderAction === 'function' || !!action;

    const handleSelect = useCallback(() => {
      if (disabled) return;
      onTabSelect?.(id);
      onChange?.(id);
    }, [disabled, id, onChange, onTabSelect]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      handleSelect();
      (props as HTMLAttributes<HTMLDivElement>).onClick?.(e);
    };

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
      if (disabled || !tabRef.current) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSelect();
      }
      props.onKeyDown?.(e);
    };

    const handleActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (disabled) return;

      action?.onClick?.(id, e);
    };

    const actionIconProps = {
      size: 16,
      color: disabled
        ? semantic_colors.content.disabled
        : selected
        ? semantic_colors.accent.primary
        : semantic_colors.content.primary,
    };

    return (
      <TabWrapper
        {...props}
        className={classNames('pds-box-tab', className)}
        role='tab'
        aria-selected={selected}
        aria-disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        ref={mergedRef}
        id={id}
        $selected={selected}
        $disabled={disabled}
        $padding={action ? '8px 8px 8px 12px' : '8px 12px'}
      >
        {selected && <TabContentBackground />}
        <TabContent>
          <Label $selected={selected} $disabled={disabled}>
            {children}
          </Label>
          {showAction && (
            <>
              {typeof renderAction === 'function' ? (
                <ActionWrapper onClick={(e) => e.stopPropagation()}>
                  {renderAction({ iconComponent: ActionIcon, iconProps: actionIconProps })}
                </ActionWrapper>
              ) : (
                <ActionButton onClick={handleActionClick}>
                  <ActionIcon {...actionIconProps} />
                </ActionButton>
              )}
            </>
          )}
        </TabContent>
      </TabWrapper>
    );
  },
);

BoxTab.displayName = 'BoxTab';

const TabWrapper = styled.div<{ $selected: boolean; $disabled: boolean; $padding: string }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
  padding: ${({ $padding }) => $padding};
  margin-bottom: ${({ $selected }) => ($selected ? '0' : '1px')};
  border: 1px solid ${semantic_colors.border.primary};
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.1s linear;

  background-color: ${({ $selected, $disabled }) => {
    if ($selected) return semantic_colors.background.surface;
    if ($disabled) return semantic_colors.background.disabled;
    return semantic_colors.fill.tertiary;
  }};

  &:hover {
    background-color: ${({ $selected, $disabled }) => !$selected && !$disabled && semantic_colors.background.surface};
  }

  &:active {
    background-color: ${({ $selected, $disabled }) => !$selected && !$disabled && semantic_colors.fill.secondary};
  }

  &:first-of-type {
    margin-left: 0;
  }
`;

const TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 3;
`;

const TabContentBackground = styled.div`
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 100%;
  height: 1px;
  background-color: ${semantic_colors.background.surface};
`;

const Label = styled.span<{ $selected: boolean; $disabled: boolean }>`
  ${text_styles.Body_14_Medium};
  color: ${({ $selected, $disabled }) => {
    if ($disabled) {
      return $selected ? semantic_colors.accent.disabled : semantic_colors.content.disabled;
    }
    return $selected ? semantic_colors.accent.primary : semantic_colors.content.primary;
  }};
  white-space: nowrap;
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: inherit;
`;
